import React, { useEffect, useState } from 'react';
import { teacherApi } from '../../api/client';
import Spinner from '../../components/common/Spinner';
import { FaCheck, FaTimes, FaClock, FaUserCheck } from 'react-icons/fa';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  present: { label:'P', color:'#10b981', bg:'#d1fae5', icon: FaCheck },
  absent:  { label:'A', color:'#ef4444', bg:'#fee2e2', icon: FaTimes },
  late:    { label:'L', color:'#f59e0b', bg:'#fef3c7', icon: FaClock },
  excused: { label:'E', color:'#8b5cf6', bg:'#ede9fe', icon: FaCheck },
};

export default function Attendance() {
  const [grades,      setGrades]      = useState([]);
  const [gradeId,     setGradeId]     = useState('');
  const [month,       setMonth]       = useState(new Date().toISOString().slice(0,7));
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState({});
  const [bulkSaving,  setBulkSaving]  = useState(false);

  useEffect(() => {
    teacherApi.getGrades().then(r => setGrades(r.data.data));
  }, []);

  useEffect(() => {
    if (!gradeId) return;
    setLoading(true);
    teacherApi.getAttendance(gradeId, month)
      .then(r => setData(r.data.data))
      .finally(() => setLoading(false));
  }, [gradeId, month]);

  const getDays = () => {
    if (!data) return [];
    const [y,m] = month.split('-');
    const days = new Date(y, m, 0).getDate();
    return Array.from({ length: days }, (_, i) => `${month}-${String(i+1).padStart(2,'0')}`);
  };

  const markAttendance = async (learnerId, date, status) => {
    const key = `${learnerId}-${date}`;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await teacherApi.saveAttendance({ learner_id: learnerId, grade_id: gradeId, date, status });
      setData(prev => ({
        ...prev,
        attendance: { ...prev.attendance, [learnerId]: { ...prev.attendance[learnerId], [date]: { status } } }
      }));
    } catch { toast.error('Failed to save'); }
    finally { setSaving(s => { const n={...s}; delete n[key]; return n; }); }
  };

  // Mark ALL learners present for today in one batch request
  const markAllPresentToday = async () => {
    if (!data?.learners?.length) return;
    setBulkSaving(true);
    const records = data.learners.map(l => ({
      learner_id: l.user_id,
      status: 'present',
      note: '',
    }));
    try {
      await teacherApi.bulkAttendance({ records, grade_id: gradeId, date: today });
      // Update local state
      const updated = { ...data.attendance };
      data.learners.forEach(l => {
        updated[l.user_id] = { ...updated[l.user_id], [today]: { status: 'present' } };
      });
      setData(prev => ({ ...prev, attendance: updated }));
      toast.success(`Marked ${records.length} learners present`);
    } catch { toast.error('Bulk save failed'); }
    finally { setBulkSaving(false); }
  };

  const today = new Date().toISOString().slice(0,10);
  const days  = getDays();

  return (
    <div>
      <div className="page-header"><h2>Mark Attendance</h2></div>

      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div>
          <label className="sms-label">Grade</label>
          <select className="sms-input" style={{ width:220 }} value={gradeId} onChange={e=>setGradeId(e.target.value)}>
            <option value="">Select Grade</option>
            {grades.map(g=><option key={g.grade_id} value={g.grade_id}>Grade {g.grade_number}</option>)}
          </select>
        </div>
        <div>
          <label className="sms-label">Month</label>
          <input className="sms-input" type="month" value={month} onChange={e=>setMonth(e.target.value)} style={{ width:180 }} />
        </div>
        {gradeId && data && month === today.slice(0,7) && (
          <button
            onClick={markAllPresentToday}
            disabled={bulkSaving}
            className="sms-btn sms-btn-primary"
            style={{ background:'#10b981', alignSelf:'flex-end' }}>
            {bulkSaving ? 'Saving...' : <><FaUserCheck size={14}/> Mark All Present Today</>}
          </button>
        )}
      </div>

      {!gradeId && <div className="sms-card" style={{ textAlign:'center', color:'#9ca3af', padding:'60px 0' }}>Select a grade to mark attendance</div>}
      {gradeId && loading && <Spinner />}
      {gradeId && !loading && data && (
        <div style={{ overflowX:'auto' }}>
          <table className="sms-table" style={{ minWidth: 800 }}>
            <thead>
              <tr>
                <th style={{ position:'sticky', left:0, background:'#f7f8fa', zIndex:2, minWidth:160 }}>Learner</th>
                {days.map(d => {
                  const day = new Date(d);
                  const isToday = d === today;
                  const isWeekend = day.getDay()===0||day.getDay()===6;
                  return (
                    <th key={d} style={{ textAlign:'center', padding:'8px 4px', minWidth:38, background:isToday?'#eff6ff':isWeekend?'#f9fafb':'#f7f8fa', color:isWeekend?'#d1d5db':isToday?'#0d6efd':'inherit' }}>
                      <div>{day.getDate()}</div>
                      <div style={{ fontSize:'0.65rem', fontWeight:400 }}>{day.toLocaleString('en',{weekday:'short'})}</div>
                    </th>
                  );
                })}
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {data.learners.map(l => {
                const att = data.attendance[l.user_id] || {};
                const totalMarked = Object.keys(att).length;
                const present = Object.values(att).filter(a=>a.status==='present').length;
                const pct = totalMarked > 0 ? Math.round((present/totalMarked)*100) : null;
                return (
                  <tr key={l.user_id}>
                    <td style={{ position:'sticky', left:0, background:'#fff', fontWeight:500, zIndex:1 }}>{l.first_name} {l.surname}</td>
                    {days.map(d => {
                      const rec = att[d];
                      const isFuture = d > today;
                      const key = `${l.user_id}-${d}`;
                      const day = new Date(d).getDay();
                      if (day===0||day===6) return <td key={d} style={{ background:'#f9fafb' }}></td>;
                      return (
                        <td key={d} style={{ textAlign:'center', padding:'4px 2px' }}>
                          {isFuture ? <span style={{ color:'#e5e7eb', fontSize:'0.75rem' }}>-</span> : (
                            <div style={{ display:'flex', gap:2, justifyContent:'center', flexWrap:'wrap' }}>
                              {Object.entries(STATUS_CONFIG).map(([s, c]) => (
                                <button key={s} onClick={() => markAttendance(l.user_id, d, s)} disabled={!!saving[key]}
                                  style={{ width:22, height:22, borderRadius:4, border:`1px solid ${rec?.status===s?c.color:'#e5e7eb'}`, background:rec?.status===s?c.bg:'transparent', cursor:'pointer', fontSize:'0.65rem', fontWeight:700, color:rec?.status===s?c.color:'#d1d5db', transition:'all 0.1s' }}
                                  title={s.charAt(0).toUpperCase()+s.slice(1)}>
                                  {c.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td style={{ textAlign:'center', fontWeight:600, color: pct!==null?(pct>=75?'#10b981':pct>=50?'#f59e0b':'#ef4444'):'#9ca3af' }}>
                      {pct !== null ? `${pct}%` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
